import { hasAnalyticsConsent } from "@/lib/consent";
/**
 * The only way the app talks to Google Tag Manager.
 *
 * GA4 receives a short, fixed list of events. Anything not on this list does
 * not reach the dataLayer, so a stray push somewhere in the app cannot turn
 * into a GA event. The GTM container fires one tag per group here and nothing
 * else (see docs/tracking.md).
 *
 * `purchase` is not here on purpose: it is sent by the server (stripe-webhook,
 * GA4 Measurement Protocol) when Stripe confirms the money, so a closed tab, an
 * ad blocker or a 3-D Secure redirect cannot lose it, and a page refresh
 * cannot count it twice.
 */
export type GtmEvent =
  // Site
  | "virtual_pageview" // → GA4 page_view (the config tag sends none of its own)
  | "cta_click" // a click that heads to a conversion page, or on a [data-track] element
  | "demo_open" // a sample report opened on a marketing page (analysis_type, cta_location)
  | "generate_lead" // free audit submitted with an email (lead_type=free_audit)
  | "free_audit_start" // free audit form sent with a URL and an email (before the eligibility check)
  | "free_audit_completed" // the free audit finished: sent by the server (free-audit-funnel), never pushed from here
  // The other free audit steps (form start and errors, report opened, fixes clicked, signup) are
  // not in the GTM container's list: src/lib/freeAuditFunnel.ts sends them through the server.
  | "newsletter_signup" // a new newsletter subscriber (form_location), see newsletter.ts
  // Account
  | "sign_up" // first arrival of a verified new account
  | "login" // a real sign-in (not a page reload with an existing session)
  // In-app milestones (full detail goes to product_events, see product-analytics.ts)
  | "analysis_submitted"
  | "report_exported"
  | "report_shared"
  | "upgrade_clicked";

/** GA4 ecommerce events, pushed with an `ecommerce` object (see pushEcommerce). */
export type GtmEcommerceEvent = "view_item_list" | "select_item" | "begin_checkout";

declare global {
  interface Window {
    __ecNoTrack?: boolean;
    __ecNoTrackReason?: string;
    /** Set by the gate in index.html on the first key, click, tap, wheel or real mouse move. */
    __ecHuman?: boolean;
  }
}

/** Set by the gate in index.html: bot, non-production host or internal device. */
export function isTrackingBlocked(): boolean {
  if (typeof window === "undefined") return true;
  return window.__ecNoTrack === true;
}

let humanWait: Promise<void> | null = null;

/**
 * Resolves once the visitor has shown they are a person (index.html sets
 * window.__ecHuman and fires "ec:human"). A script that loads a page, scrolls
 * it and leaves never resolves it, so nothing it does is sent. Pages that do
 * not run the gate (tests, local dev) are not held back.
 */
export function whenHuman(): Promise<void> {
  if (typeof window === "undefined") return new Promise(() => {});
  if (window.__ecHuman || window.__ecNoTrack === undefined) return Promise.resolve();
  if (!humanWait) {
    humanWait = new Promise((resolve) => window.addEventListener("ec:human", () => resolve(), { once: true }));
  }
  return humanWait;
}

const EMPTY_PARAMS: Record<string, undefined> = {
  cta_text: undefined,
  cta_location: undefined,
  cta_destination: undefined,
  cta_id: undefined,
  method: undefined,
  plan: undefined,
  value: undefined,
  currency: undefined,
  lead_type: undefined,
  form_location: undefined,
  analysis_type: undefined,
  is_first: undefined,
  export_format: undefined,
  share_method: undefined,
  trigger: undefined,
};

/**
 * Every GA event is also recorded in our own analytics (the Admin), which
 * counts visitors without consent too (cookieless, see ownAnalytics.ts).
 * Page views and logins are already sent there by TrackingManager.
 */
const NOT_MIRRORED = new Set<string>(["virtual_pageview", "login"]);
function mirrorToOwnAnalytics(event: string, params: Record<string, unknown>): void {
  if (NOT_MIRRORED.has(event)) return;
  void import("@/lib/ownAnalytics").then((m) => m.sendOwnEvent(event, params)).catch(() => {});
}

export function pushEvent(event: GtmEvent, params: Record<string, unknown> = {}): void {
  if (isTrackingBlocked()) return;
  window.dataLayer = window.dataLayer || [];
  // GA4 keeps the last value of a dataLayer key until it is overwritten, so a
  // cta_text from one click would ride along on the next page_view. Clearing
  // the event-scoped keys on every push keeps each event to its own params.
  window.dataLayer.push({ ...EMPTY_PARAMS, event, ...params });
  mirrorToOwnAnalytics(event, params);
}

/** Fire once per key per browser (localStorage), e.g. sign_up per user id. */
export function pushEventOnce(key: string, event: GtmEvent, params: Record<string, unknown> = {}): void {
  const storageKey = `ec_evt:${key}`;
  // The dedup marker is analytics storage: only kept with consent (PECR).
  if (!hasAnalyticsConsent()) {
    pushEvent(event, params);
    return;
  }
  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, new Date().toISOString());
  } catch {
    /* storage blocked: fire anyway, a duplicate is better than nothing */
  }
  pushEvent(event, params);
}

// ── Ecommerce ────────────────────────────────────────────────────────────

export type GaItem = {
  item_id: string; // plan or pack slug
  item_name: string;
  item_category: "subscription" | "credit_pack";
  item_variant?: "monthly" | "yearly" | "one_off";
  price: number;
  quantity: 1;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
};

export function planItem(o: {
  slug: string;
  name: string;
  price: number;
  pack?: boolean;
  yearly?: boolean;
  index?: number;
}): GaItem {
  return {
    item_id: o.slug,
    item_name: o.name,
    item_category: o.pack ? "credit_pack" : "subscription",
    item_variant: o.pack ? "one_off" : o.yearly ? "yearly" : "monthly",
    price: Math.round((Number(o.price) || 0) * 100) / 100,
    quantity: 1,
    ...(o.index !== undefined ? { index: o.index } : {}),
  };
}

export function pushEcommerce(
  event: GtmEcommerceEvent,
  data: { currency: string; items: GaItem[]; value?: number; item_list_id?: string; item_list_name?: string },
): void {
  if (isTrackingBlocked()) return;
  window.dataLayer = window.dataLayer || [];
  const value = data.value ?? data.items.reduce((s, i) => s + i.price * i.quantity, 0);
  // Google's rule for ecommerce pushes: clear the previous ecommerce object
  // first, or its items merge into this event.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    ...EMPTY_PARAMS,
    event,
    ecommerce: {
      currency: data.currency.toUpperCase(),
      value: Math.round(value * 100) / 100,
      ...(data.item_list_id ? { item_list_id: data.item_list_id, item_list_name: data.item_list_name } : {}),
      items: data.items,
    },
  });
  mirrorToOwnAnalytics(event, {
    currency: data.currency.toUpperCase(),
    value: Math.round(value * 100) / 100,
    item_list_name: data.item_list_name,
    items: data.items.map((i) => i.item_id).join(","),
  });
}

// ── Identity ─────────────────────────────────────────────────────────────

/**
 * The signed-in user's id on every later GA hit (cross-device reporting).
 * A Supabase uuid, never an email. Kept as a persistent dataLayer key; the
 * GA4 tags read it through {{DLV user_id}}.
 */
export function setAnalyticsUserId(userId: string | null): void {
  if (isTrackingBlocked()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ user_id: userId ?? undefined });
}

/**
 * GA's own ids for this browser, read from its cookies. Sent with a checkout
 * so the server can report the purchase to GA4 against the same user and
 * session. Empty when GA is not running (bots, internal devices, or the
 * visitor declined analytics cookies), in which case nothing is sent to GA.
 */
export function getGaIds(measurementId = "G-CD153CMM9D"): { ga_client_id?: string; ga_session_id?: string } {
  if (typeof document === "undefined" || isTrackingBlocked()) return {};
  const cookies = Object.fromEntries(
    document.cookie.split(/;\s*/).filter(Boolean).map((c) => {
      const i = c.indexOf("=");
      return [c.slice(0, i), decodeURIComponent(c.slice(i + 1))];
    }),
  );
  const out: { ga_client_id?: string; ga_session_id?: string } = {};
  // _ga = GA1.1.<random>.<timestamp>  → client id "<random>.<timestamp>"
  const ga = cookies["_ga"]?.match(/^GA\d\.\d\.(\d+\.\d+)$/);
  if (ga) out.ga_client_id = ga[1];
  // _ga_<ID> = GS1.1.<session_id>.<n>...  or  GS2.1.s<session_id>$o<n>...
  const sess = cookies[`_ga_${measurementId.replace(/^G-/, "")}`];
  const m = sess?.match(/^GS1\.\d\.(\d+)\./) || sess?.match(/^GS2\.\d\.s(\d+)/);
  if (m) out.ga_session_id = m[1];
  return out;
}

/**
 * Stop measuring this browser from now on: the current page stops pushing and
 * the gate in index.html will not load GTM on later visits. Called when an
 * admin signs in, so the founder's own visits stay out of GA.
 */
export function markInternalDevice(): void {
  if (typeof window === "undefined") return;
  window.__ecNoTrack = true;
  window.__ecNoTrackReason = "internal";
  try {
    localStorage.setItem("ec_internal", "1");
  } catch {
    /* ignore */
  }
}
